import { Inject, Injectable, OpaqueToken } from "@angular/core";
import { Headers, Http } from "@angular/http";
import { Observable, Subject } from "rxjs";
import "rxjs/add/operator/map";
import { AbstractHttpService } from "../http/abstract-http.service";
import { LocalConfigurationService } from "../configuration/local-configuration.service";
import { ResourceModule } from "../resource/resource.module";
import { ReplaySubject } from "rxjs/ReplaySubject";
/**
 * Authentication Service to obtain access_token and refresh_token.
 */
export class AuthService extends AbstractHttpService {
    constructor(http, localConfigurationService, nameSpace, entityNames, searchNames, actionNames, httpTimeout) {
        super();
        this.http = http;
        this.localConfigurationService = localConfigurationService;
        this.nameSpace = nameSpace;
        this.entityNames = entityNames;
        this.searchNames = searchNames;
        this.actionNames = actionNames;
        this.httpTimeout = httpTimeout;
        this.loginChecked = false;
        this.refreshOptions = {};
        this.pathName = window.location.pathname;
        this.storage = localStorage;
        // set tokens if saved in local storage
        let primaryUserAccessStorage = JSON.parse(this.storage.getItem(this.primaryUserAccessStorageKey));
        this.primaryUserAccessToken = primaryUserAccessStorage && primaryUserAccessStorage.primary_user_access_token;
        let accessStorage = JSON.parse(this.storage.getItem(this.accessStorageKey));
        this.accessToken = accessStorage && accessStorage.access_token;
        let refreshStorage = JSON.parse(this.storage.getItem(this.refreshStorageKey));
        this.refreshToken = refreshStorage && refreshStorage.refresh_token;
        this.restrictedOutlets = JSON.parse(this.storage.getItem(this.restrictedOutletsStorageKey));
        this.restrictedCustomOutletGroups = JSON.parse(this.storage.getItem(this.restrictedCustomOutletGroupsStorageKey));
        this._isRestrictedLocalisation = this.restrictedOutlets != undefined;
        this._loginChanging$ = new Subject();
        this._loginDetails$ = new Subject();
        this._currentLoginDetails$ = new ReplaySubject(1);
        this._loginRevoked$ = new Subject();
        this._compatibility$ = new Subject();
    }
    get isRestrictedLocalisation() {
        return this._isRestrictedLocalisation;
    }
    /**
     * Returns an observable sequence indicating that a login or logout has been initiated
     * @returns {Observable}
     */
    get loginChanging$() {
        return this._loginChanging$;
    }
    /**
     * Returns an observable sequence of login details.
     * @returns {Observable}
     */
    get loginDetails$() {
        return this._loginDetails$;
    }
    /**
     * Returns a replay subject containing the current login details
     * @returns {Subject}
     */
    get currentLoginDetails$() {
        return this._currentLoginDetails$;
    }
    /**
     * Returns an observable sequence indicating that the user login has been revoked
     * @returns {Observable}
     */
    get loginRevoked$() {
        return this._loginRevoked$;
    }
    /**
     * Returns an observable sequence of compatibility
     * @returns {Observable}
     */
    get compatibility$() {
        return this._compatibility$;
    }
    get primaryUserAccessStorageKey() {
        return this.pathName + "/" + AuthService.PRIMARY_USER_ACCESS_STORAGE_KEY;
    }
    get accessStorageKey() {
        return this.pathName + "/" + AuthService.ACCESS_STORAGE_KEY;
    }
    get refreshStorageKey() {
        return this.pathName + "/" + AuthService.REFRESH_STORAGE_KEY;
    }
    get dataserverUrlStorageKey() {
        return this.pathName + "/" + AuthService.DATASERVER_URL_STORAGE_KEY;
    }
    get restrictedOutletsStorageKey() {
        return this.pathName + "/" + AuthService.RESTRICTED_OUTLETS_STORAGE_KEY;
    }
    get restrictedCustomOutletGroupsStorageKey() {
        return this.pathName + "/" + AuthService.RESTRICTED_CUSTOM_OUTLET_GROUPS_STORAGE_KEY;
    }
    get dataserverURL() {
        const storedDataserverURL = this.storage.getItem(this.dataserverUrlStorageKey);
        if (!this._dataserverURL) {
            this._dataserverURL = storedDataserverURL;
        }
        else if (this._dataserverURL !== storedDataserverURL) {
            this.dataserverURL = this._dataserverURL;
        }
        return this._dataserverURL;
    }
    set dataserverURL(dataserverURL) {
        this._dataserverURL = dataserverURL || '';
        this.storage.setItem(this.dataserverUrlStorageKey, this._dataserverURL);
    }
    notifyLoginDetailsChange() {
        const loginDetails = new LoginDetails(this.getLoggedInUserName(), this.getLoggedInUserId(), this.getLoggedInIdentityId(), this.getLoggedInRole(), this.getLoggedInMember(), this.getLoggedInLocationId(), this.getLoggedInOutletId(), this.getLoggedInWebTerminalId());
        this._loginDetails$.next(loginDetails);
        this._currentLoginDetails$.next(loginDetails);
    }
    login(user, userPassword, loginType, options = {}) {
        console.log("Logging in...");
        this._loginChanging$.next(true);
        if (loginType === LoginType.POS_PIN) {
            // Store the current access token as the primary user access token (will be restored on logout of the pos pin user)
            if (this.getLoggedInType() === LoginType.USER) {
                this.primaryUserAccessToken = this.accessToken;
                this.storage.setItem(this.primaryUserAccessStorageKey, JSON.stringify({
                    primary_user_access_token: this.primaryUserAccessToken
                }));
            }
            // Get a new access token for the POS pin user
            return this.refreshAccessToken({
                loginType: loginType,
                userPin: options.userPin
            }).catch((error) => this.handleServiceError(error));
        }
        if (options.remember === false) {
            this.storage = sessionStorage;
        }
        let headers = new Headers();
        headers.append('Content-Type', 'application/json');
        return Observable.fromPromise(this.localConfigurationService.getString('local/dataserverURL').then((dataserverURL) => {
            this.dataserverURL = dataserverURL;
            let loginURL = this.dataserverURL + AuthService.LOGIN_URL;
            let request = this.http.post(loginURL, JSON.stringify({
                user: user,
                userPassword: userPassword,
                appId: options.appId,
                deviceId: options.deviceId,
                webTerminalName: options.webTerminalName,
                appName: this.localConfigurationService.application,
                configContextNames: this.localConfigurationService.contextNames,
                loginType: LoginType[loginType],
                identity: options.identity,
                identityPassword: options.identityPassword,
                verificationId: options.verificationId,
                verificationCode: options.verificationCode,
                entityResourceNames: this.includeNameSpaceToResourceNames(this.entityNames),
                actionResourceNames: this.includeNameSpaceToResourceNames(this.actionNames),
                searchResourceNames: this.includeNameSpaceToResourceNames(this.searchNames)
            }), { headers });
            if (this.httpTimeout) {
                request = request.timeout(this.httpTimeout);
            }
            return request.map((response) => {
                // login successful if there's a jwt token in the response
                let json = response.json();
                let received_access_token = json && json.access_token;
                let received_refresh_token = json && json.refresh_token;
                if (received_access_token && received_refresh_token) {
                    // set token property
                    this.accessToken = received_access_token;
                    this.refreshToken = received_refresh_token;
                    this.storage.setItem(this.accessStorageKey, JSON.stringify({
                        access_token: received_access_token
                    }));
                    this.storage.setItem(this.refreshStorageKey, JSON.stringify({
                        user: user,
                        identity: options.identity,
                        refresh_token: received_refresh_token
                    }));
                    this.notifyLoginDetailsChange();
                    return true;
                }
                else {
                    //this is not meant to happen. It means server message protocol has been changed.
                    this.logout();
                    return false;
                }
            }).catch((error) => {
                if (error && error.status === 406) {
                    this._compatibility$.next(false);
                    return this.handleServiceError(error);
                }
                else {
                    this.logout();
                    return this.handleServiceError(error);
                }
            });
        })).flatMap((result) => result);
    }
    /**
     * Client side detection whether access token is expired or about to expire
     * (i.e. considers expired a minute ahead of actual expiry).
     *
     * @returns {boolean}
     */
    isAccessTokenExpired() {
        if (!this.accessToken) {
            return true; //consider expired if we are not authenticated yet ...
        }
        let parsedToken = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return true; //consider expired if access_token is not in valid jwt format.
        }
        //divide by 1000 because of the way jwt dates are stored
        //subtract 60 seconds (i.e. 1 minutes) to expire token a minute ahead of actual expiry ...
        return (parsedToken.exp - 60) <= Math.floor(Date.now() / 1000);
    }
    getRestrictedOutlets() {
        return this.restrictedOutlets;
    }
    getRestrictedCustomOutletGroups() {
        return this.restrictedCustomOutletGroups;
    }
    refreshAccessToken(options) {
        if (this.refreshToken == null) {
            return Observable.throw("Attempted access-token refresh without a refresh-token");
        }
        if (options) {
            this.refreshOptions = options;
        }
        let headers = new Headers();
        headers.append('Content-Type', 'application/json');
        if (this.accessTokenRefreshObservable) {
            return this.accessTokenRefreshObservable;
        }
        this.accessTokenRefreshObservable = Observable.fromPromise(this.localConfigurationService.getString('local/dataserverURL').then((dataserverURL) => {
            this.dataserverURL = dataserverURL;
            let refreshURL = this.dataserverURL + AuthService.REFRESH_URL;
            let request = this.http.post(refreshURL, JSON.stringify({
                refresh_token: this.refreshToken,
                loginType: this.refreshOptions.loginType ? LoginType[this.refreshOptions.loginType] : undefined,
                userPin: this.refreshOptions.userPin,
                entityResourceNames: this.includeNameSpaceToResourceNames(this.entityNames),
                actionResourceNames: this.includeNameSpaceToResourceNames(this.actionNames),
                searchResourceNames: this.includeNameSpaceToResourceNames(this.searchNames),
                restrictedOutlets: this.restrictedOutlets,
                restrictedCustomOutletGroups: this.restrictedCustomOutletGroups
            }), { headers });
            if (this.httpTimeout) {
                request = request.timeout(this.httpTimeout);
            }
            return request.flatMap((response) => {
                // login successful if there's a jwt token in the response
                let receivedAccessToken = response.json() && response.json().access_token;
                if (receivedAccessToken) {
                    //console.log("Refresh token request successful ... ");
                    // set token property
                    this.accessToken = receivedAccessToken;
                    // store access token in local storage
                    this.storage.setItem(this.accessStorageKey, JSON.stringify({
                        access_token: this.accessToken
                    }));
                    this.accessTokenRefreshObservable = null;
                    if (this.refreshOptions.loginType === LoginType.POS_PIN) {
                        this.notifyLoginDetailsChange();
                    }
                    // check if the restriction was successful
                    if (this.isRestrictedLocalisation) {
                        if (!this.isRestrictedLocalisationToken()) {
                            this.clearInternalRestrictedLocalisation();
                        }
                    }
                    return Observable.of(true);
                }
                else {
                    //this is not meant to happen.
                    this.accessTokenRefreshObservable = null;
                    return Observable.of(false);
                }
            }).catch((error) => {
                if (error && error.status === 406) {
                    this._compatibility$.next(false);
                }
                else if (error && (error.status === 400 || error.status === 401) && this.refreshOptions.loginType !== LoginType.POS_PIN) {
                    //we have our refresh token expired or revoked ... we should logout and rethrow this error further ...
                    this.logout();
                    this._loginRevoked$.next(true);
                }
                this.accessTokenRefreshObservable = null;
                return Observable.throw(error);
            });
        })).flatMap((result) => result).publishReplay(1).refCount();
        return this.accessTokenRefreshObservable;
    }
    /**
     * Set the location context for this session.
     * @param locationId
     * @param outletId
     * @param webTerminalId
     */
    setLocationContext(locationId, outletId, webTerminalId) {
        if (locationId != undefined) {
            this.locationId = locationId;
        }
        if (outletId != undefined) {
            this.outletId = outletId;
        }
        if (webTerminalId != undefined) {
            this.webTerminalId = webTerminalId;
        }
        this.refreshAccessToken({}).subscribe(() => {
        }, (error) => {
        });
    }
    /**
     * Clear the location context
     */
    clearLocationContext() {
        this.locationId = undefined;
        this.outletId = undefined;
        this.webTerminalId = undefined;
        this.refreshAccessToken({}).subscribe(() => {
        }, (error) => {
        });
    }
    setDefaultLocalDateTime(defaultLocalDateTime) {
        if (defaultLocalDateTime != undefined) {
            this.defaultLocalDateTime = defaultLocalDateTime;
        }
        this.refreshAccessToken({}).subscribe(() => {
        }, (error) => {
        });
    }
    clearDefaultLocalDateTime() {
        this.defaultLocalDateTime = undefined;
        this.refreshAccessToken({}).subscribe(() => {
        }, (error) => {
        });
    }
    /**
     * Set the restricted localisation
     * @param {number[]} outlets
     * @param {number[]} customOutletGroups
     */
    setRestrictedLocalisation(outlets, customOutletGroups) {
        this.restrictedOutlets = outlets;
        this.restrictedCustomOutletGroups = customOutletGroups;
        this.storage.setItem(this.restrictedOutletsStorageKey, JSON.stringify(this.restrictedOutlets));
        this.storage.setItem(this.restrictedCustomOutletGroupsStorageKey, JSON.stringify(this.restrictedCustomOutletGroups));
        this._isRestrictedLocalisation = true;
        this.refreshAccessToken({}).subscribe(() => {
        }, (error) => {
        });
    }
    /**
     * Clear the restricted localisation
     */
    clearRestrictedLocalisation() {
        this.clearInternalRestrictedLocalisation();
        this.refreshAccessToken({}).subscribe(() => {
        }, (error) => {
        });
    }
    logout() {
        console.log("Logging out...");
        this._loginChanging$.next(true);
        if (this.getLoggedInType() === LoginType.POS_PIN) {
            // In theory, we should always have the primary user access token as we persist it during the POS_PIN login, but if not retrieve it now.
            if (!this.primaryUserAccessToken) {
                let primaryUserAccessStorage = JSON.parse(this.storage.getItem(this.primaryUserAccessStorageKey));
                this.primaryUserAccessToken = primaryUserAccessStorage && primaryUserAccessStorage.primary_user_access_token;
            }
            if (this.primaryUserAccessToken) {
                this.accessToken = this.primaryUserAccessToken;
                this.storage.setItem(this.accessStorageKey, JSON.stringify({
                    access_token: this.accessToken
                }));
                this.notifyLoginDetailsChange();
                return;
            }
        }
        if (this.isLoggedIn()) {
            //send server side logout request with this refresh token ...
            let headers = new Headers();
            headers.append('Content-Type', 'application/json');
            let logoutURL = this.dataserverURL + AuthService.LOGOUT_URL;
            this.http.post(logoutURL, JSON.stringify({ refresh_token: this.refreshToken }), { headers }).subscribe((data) => {
            }, (err) => {
                console.log("Error sending logout request to server");
            });
        }
        // clear token remove user from local storage to log user out
        this.accessToken = null;
        this.refreshToken = null;
        this.locationId = null;
        this.outletId = null;
        this.webTerminalId = null;
        this.storage.removeItem(this.accessStorageKey);
        this.storage.removeItem(this.refreshStorageKey);
        this.clearInternalRestrictedLocalisation();
        this.notifyLoginDetailsChange();
    }
    isLoggedIn() {
        if (!this.refreshToken) {
            return false;
        }
        if (!this.loginChecked) {
            this.notifyLoginDetailsChange();
            this.loginChecked = true;
        }
        return true;
    }
    getLoggedInUserId() {
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return parsedToken.userId;
    }
    getLoggedInUserName() {
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return parsedToken.username;
    }
    getLoggedInIdentityId() {
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return parsedToken.identityId;
    }
    getLoggedInRole() {
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return parsedToken.role;
    }
    getLoggedInMember() {
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return parsedToken.memberNo;
    }
    isUniversal() {
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return parsedToken.universal;
    }
    getOutlets() {
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return parsedToken.outlets;
    }
    getCustomOutletGroups() {
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return parsedToken.customOutletGroups;
    }
    getLoggedInLocationId() {
        if (this.locationId) {
            return this.locationId;
        }
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return parsedToken.locationId;
    }
    getLoggedInOutletId() {
        if (this.outletId) {
            return this.outletId;
        }
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return parsedToken.outletId;
    }
    getLoggedInWebTerminalId() {
        if (this.webTerminalId) {
            return this.webTerminalId;
        }
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return parsedToken.webTerminalId;
    }
    getLoggedInWebTerminalName() {
        if (this.webTerminalId) {
            return this.webTerminalId;
        }
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return parsedToken.webTerminalName;
    }
    getLoggedInType() {
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return LoginType[parsedToken.loginType];
    }
    parseToken(token) {
        var base64Url = token.split('.')[1];
        if (base64Url) {
            return JSON.parse(window.atob(base64Url.replace('-', '+').replace('_', '/')));
        }
        return undefined;
    }
    setAuthorizationAndLocationHeaders(headers) {
        headers.delete(AuthService.HEADER_AUTHORIZATION);
        headers.append(AuthService.HEADER_AUTHORIZATION, `Bearer ` + this.accessToken);
        headers.delete(AuthService.HEADER_LOCATIONCONTEXT_LOCATIONID);
        headers.append(AuthService.HEADER_LOCATIONCONTEXT_LOCATIONID, this.locationId ? this.locationId.toString() : null);
        headers.delete(AuthService.HEADER_LOCATIONCONTEXT_OUTLETID);
        headers.append(AuthService.HEADER_LOCATIONCONTEXT_OUTLETID, this.outletId ? this.outletId.toString() : null);
        headers.delete(AuthService.HEADER_LOCATIONCONTEXT_WEBTERMINALID);
        headers.append(AuthService.HEADER_LOCATIONCONTEXT_WEBTERMINALID, this.webTerminalId ? this.webTerminalId.toString() : null);
        headers.delete(AuthService.HEADER_LOCATIONCONTEXT_DEFAULTLOCALDATETIME);
        headers.append(AuthService.HEADER_LOCATIONCONTEXT_DEFAULTLOCALDATETIME, this.defaultLocalDateTime);
    }
    clearInternalRestrictedLocalisation() {
        this.restrictedOutlets = undefined;
        this.restrictedCustomOutletGroups = undefined;
        this._isRestrictedLocalisation = false;
        this.storage.removeItem(this.restrictedOutletsStorageKey);
        this.storage.removeItem(this.restrictedCustomOutletGroupsStorageKey);
    }
    isRestrictedLocalisationToken() {
        if (!this.accessToken) {
            return undefined;
        }
        let parsedToken = this.parseToken(this.accessToken);
        if (!parsedToken) {
            return undefined;
        }
        return parsedToken.restrictedLocalisation;
    }
    includeNameSpaceToResourceNames(names) {
        return names ? names.map(value => this.nameSpace + "/" + value) : undefined;
    }
}
/**
 * Token for injected http timeout value
 * @type {OpaqueToken}
 */
AuthService.HTTP_TIMEOUT_KEY = new OpaqueToken('httpTimeout');
AuthService.LOGIN_URL = '/dataserver/auth/login';
AuthService.LOGOUT_URL = '/dataserver/auth/logout';
AuthService.REFRESH_URL = '/dataserver/auth/refresh';
AuthService.PRIMARY_USER_ACCESS_STORAGE_KEY = 'primaryUserAccessStorage';
AuthService.ACCESS_STORAGE_KEY = 'accessStorage';
AuthService.REFRESH_STORAGE_KEY = 'authStorage';
AuthService.DATASERVER_URL_STORAGE_KEY = 'dataserverURLStorage';
AuthService.RESTRICTED_OUTLETS_STORAGE_KEY = 'RestrictedOutlets';
AuthService.RESTRICTED_CUSTOM_OUTLET_GROUPS_STORAGE_KEY = 'RestrictedCustomOutletGroups';
AuthService.HEADER_AUTHORIZATION = 'Authorization';
AuthService.HEADER_LOCATIONCONTEXT_LOCATIONID = 'LocationContextLocationId';
AuthService.HEADER_LOCATIONCONTEXT_OUTLETID = 'LocationContextOutletId';
AuthService.HEADER_LOCATIONCONTEXT_WEBTERMINALID = 'LocationContextWebTerminalId';
AuthService.HEADER_LOCATIONCONTEXT_DEFAULTLOCALDATETIME = 'LocationContextDefaultLocalDateTime';
AuthService.decorators = [
    { type: Injectable },
];
/** @nocollapse */
AuthService.ctorParameters = () => [
    { type: Http, },
    { type: LocalConfigurationService, },
    { type: undefined, decorators: [{ type: Inject, args: [ResourceModule.RESOURCE_NAMESPACE_KEY,] },] },
    { type: Array, decorators: [{ type: Inject, args: [ResourceModule.ENTITY_NAMES_KEY,] },] },
    { type: Array, decorators: [{ type: Inject, args: [ResourceModule.SEARCH_NAMES_KEY,] },] },
    { type: Array, decorators: [{ type: Inject, args: [ResourceModule.ACTION_NAMES_KEY,] },] },
    { type: undefined, decorators: [{ type: Inject, args: [AuthService.HTTP_TIMEOUT_KEY,] },] },
];
export var LoginType;
(function (LoginType) {
    LoginType[LoginType["USER"] = 0] = "USER";
    LoginType[LoginType["GUEST"] = 1] = "GUEST";
    LoginType[LoginType["MEMBER_EMAIL"] = 2] = "MEMBER_EMAIL";
    LoginType[LoginType["MEMBER_MOBILE"] = 3] = "MEMBER_MOBILE";
    LoginType[LoginType["MEMBER_NO"] = 4] = "MEMBER_NO";
    LoginType[LoginType["MEMBER_NO_PLUS_MOBILE"] = 5] = "MEMBER_NO_PLUS_MOBILE";
    LoginType[LoginType["MEMBER_DELEGATED"] = 6] = "MEMBER_DELEGATED";
    LoginType[LoginType["POS_PIN"] = 7] = "POS_PIN";
})(LoginType || (LoginType = {}));
export class LoginDetails {
    constructor(user, userId, identityId, role, member, locationId, outletId, webTerminalId) {
        this.user = user;
        this.userId = userId;
        this.identityId = identityId;
        this.role = role;
        this.member = member;
        this.locationId = locationId;
        this.outletId = outletId;
        this.webTerminalId = webTerminalId;
    }
}
//# sourceMappingURL=auth.service.js.map


//////////////////
// WEBPACK FOOTER
// ./node_modules/@fedelta-pos/data-client/auth/auth.service.js
// module id = 41
// module chunks = 1